import styled from "styled-components";
import useStudentInfo from "features/teacher/hooks/student/useStudentInfo";
import Refresh from "shared/components/Refresh";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface StudentInfoProps {
    clickedStudent: string;
}

const statusMeta = (status: string) => {
    switch (status) {
        case "suspended":
            return { label: "휴학중", color: "#D97706", bg: "#FFFBEB" };
        case "graduated":
            return { label: "퇴원", color: "#64748B", bg: "#F1F5F9" };
        default:
            return { label: "재학중", color: hw.success, bg: "#F0FDF4" };
    }
};

const StudentInfo = ({ clickedStudent }: StudentInfoProps) => {
    const { student, onRefreshClick } = useStudentInfo(clickedStudent);

    if (!clickedStudent) {
        return (
            <Container>
                <Header>
                    <div>
                        <Title>학생정보</Title>
                        <Sub>오른쪽 목록에서 원생을 선택하면 상세 정보가 표시됩니다.</Sub>
                    </div>
                </Header>
                <Empty>
                    <EmptyTitle>학생을 선택하세요</EmptyTitle>
                    <EmptyText>목록에서 이름을 클릭하면 계정·보호자·수강 정보를 확인할 수 있습니다.</EmptyText>
                </Empty>
            </Container>
        );
    }

    const status = statusMeta(student.status);
    const rows = [
        { label: "이름", value: student.name },
        { label: "아이디", value: student.id },
        { label: "비밀번호", value: student.pw },
        { label: "생년월일", value: student.birth },
        { label: "전화번호", value: student.tel },
        { label: "학교", value: student.school },
        { label: "학부모 성명", value: student.parent_name },
        { label: "학부모 전화번호", value: student.parent_tel },
        { label: "셔틀버스", value: student.shuttle ? "이용" : "미이용" },
        { label: "입원일", value: student.join_date },
        { label: "레벨", value: student.level ? `Lv.${student.level}` : "" },
        { label: "수강료", value: student.fee },
        { label: "주소", value: [student.address, student.address_detail].filter(Boolean).join(" ") },
    ];

    return (
        <Container>
            <Header>
                <div>
                    <Title>학생정보</Title>
                    <Sub>{student.name || "선택한 원생"}의 계정 정보입니다.</Sub>
                </div>
                <HeaderRight>
                    <StatusBadge $color={status.color} $bg={status.bg}>{status.label}</StatusBadge>
                    <Refresh onRefreshClick={onRefreshClick} />
                </HeaderRight>
            </Header>
            <Grid>
                {rows.map((row) => (
                    <Field key={row.label} $wide={row.label === "주소"}>
                        <Label>{row.label}</Label>
                        <Value>{row.value || "-"}</Value>
                    </Field>
                ))}
            </Grid>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 22px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    overflow: auto;
    min-height: 0;
    align-content: start;
    ${scrollbar}

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div<{ $wide?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: ${hw.bg};
    border: 1px solid ${hw.border};
    border-radius: 12px;
    min-width: 0;
    grid-column: ${props => props.$wide ? "1 / -1" : "auto"};
`;

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${hw.textMuted};
`;

const Value = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${hw.text};
    word-break: break-all;
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    font-weight: 700;
    font-size: 12px;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    min-height: 240px;
`;

const EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

export default StudentInfo;
